import { useEffect, useMemo, useRef, useState } from "react";
import { getNearbyStops } from "../utils/bods";
import { countRouteVehicles, getLiveEstimateForService, normaliseRoute } from "../utils/liveEta";

const ROUTE_COLOURS = {
  "1": "#003087", "1A": "#003087", "1S": "#003087", "2": "#003087", "5": "#003087", "6": "#003087",
  "8": "#003087", "9": "#003087", "14": "#003087", "20": "#003087", "20A": "#003087", "20B": "#003087",
  "21": "#003087", "21A": "#003087", "27": "#003087", "28": "#003087", "30": "#003087", "31": "#003087",
  "33": "#185FA5", "34": "#185FA5", "34A": "#185FA5", "35": "#185FA5", "35A": "#185FA5",
  "41": "#003087", "42": "#003087", "42A": "#003087", "42B": "#003087", "42C": "#003087", "42D": "#003087",
  "43": "#185FA5", "43A": "#185FA5", "44": "#185FA5", "48": "#185FA5", "50": "#1D9E75", "51": "#1D9E75", "61": "#1D9E75",
  "70": "#2c6e16", "70A": "#2c6e16", "70B": "#2c6e16", "70S": "#2c6e16",
};

const TYPE_CLASS = {
  Normal: "type-normal",
  School: "type-school",
  "Park & Ride": "type-parkride",
  Circular: "type-circular",
  Special: "type-special",
};

const RADIUS_OPTIONS = [0.25, 0.4, 0.8, 1.2, 1.6];
const MAX_STOPS_BY_RADIUS = { 0.25: 8, 0.4: 10, 0.8: 14, 1.2: 18, 1.6: 24 };

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
let leafletReady = false;
let leafletCallbacks = [];

function loadLeaflet(cb) {
  if (window.L || leafletReady) { leafletReady = true; return cb(); }
  leafletCallbacks.push(cb);
  if (leafletCallbacks.length > 1) return;

  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }

  const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
  const finish = () => {
    leafletReady = true;
    const callbacks = leafletCallbacks;
    leafletCallbacks = [];
    callbacks.forEach((fn) => fn());
  };
  if (existing) {
    existing.addEventListener("load", finish, { once: true });
    return;
  }
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = finish;
  document.head.appendChild(script);
}

function createStopIcon(isMain = false) {
  // Alin's bus-stop pin: white teardrop map-pin with a black bus silhouette
  // and two headlights. `isMain` = the selected/anchored stop → red pin.
  const L = window.L;
  const w = isMain ? 42 : 34;
  const h = Math.round((w * 320) / 240); // keep 240x320 aspect
  const pin = `M120 8 C68 8 28 48 28 100 C28 152 88 230 120 312 C152 230 212 152 212 100 C212 48 172 8 120 8 Z`;
  const fill = isMain ? "#D32F2F" : "#000000";
  const html = `<div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45));">
    <svg width="${w}" height="${h}" viewBox="0 0 240 320" xmlns="http://www.w3.org/2000/svg">
      <!-- Map pin -->
      <path fill="${fill}" d="${pin}"/>
      <!-- Inner white circle -->
      <circle cx="120" cy="100" r="68" fill="#ffffff"/>
      <!-- Bus body -->
      <rect x="78" y="62" width="84" height="62" rx="10" ry="10" fill="#000000"/>
      <!-- Windows -->
      <rect x="86" y="70" width="30" height="28" rx="2" ry="2" fill="#ffffff"/>
      <rect x="124" y="70" width="30" height="28" rx="2" ry="2" fill="#ffffff"/>
      <!-- Headlights -->
      <circle cx="96" cy="112" r="6" fill="#ffffff"/>
      <circle cx="144" cy="112" r="6" fill="#ffffff"/>
      <!-- Bumper / lower bar -->
      <rect x="86" y="122" width="68" height="6" rx="2" ry="2" fill="#000000"/>
      <!-- Wheels -->
      <rect x="90" y="128" width="16" height="10" rx="4" ry="4" fill="#000000"/>
      <rect x="134" y="128" width="16" height="10" rx="4" ry="4" fill="#000000"/>
    </svg>
  </div>`;
  return L.divIcon({
    className: "",
    html,
    iconSize: [w, h],
    iconAnchor: [w / 2, h], // bottom tip = the precise map point
  });
}

function createBusIcon(route, bearing = 0, expected = false) {
  const L = window.L;
  const safeRoute = String(route || "bus").replace(/</g, "");
  return L.divIcon({
    className: "",
    html: `<div class="leaflet-bus-pin ${expected ? "expected" : "seen"}" style="transform: rotate(${Number(bearing) || 0}deg)"><span style="transform: rotate(-${Number(bearing) || 0}deg)">${safeRoute}</span></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function MapTilePanel({ row, detailed = false, extraVehicles = [], taxi = null, zoomOnStop = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  const estimate = row.liveEstimate || null;
  const stopList = (row.stops?.length ? row.stops : [row.stop]).filter(Boolean);
  const hasBus = Number.isFinite(estimate?.vehicleLat) && Number.isFinite(estimate?.vehicleLng);
  const safeExpected = Boolean(getExpectedIfSafe(row));
  const routeLabel = (row.routes || [row.route]).join("/");

  useEffect(() => {
    let destroyed = false;
    loadLeaflet(() => {
      if (destroyed || !containerRef.current) return;
      const L = window.L;
      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, {
          zoomControl: detailed,
          attributionControl: detailed,
          dragging: detailed,
          scrollWheelZoom: false,
          doubleClickZoom: detailed,
          boxZoom: detailed,
          keyboard: detailed,
          tap: detailed,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: detailed ? '© <a href="https://openstreetmap.org">OpenStreetMap</a>' : "",
          maxZoom: 19,
        }).addTo(mapRef.current);
        layerRef.current = L.layerGroup().addTo(mapRef.current);
      }

      layerRef.current.clearLayers();
      const bounds = [];
      stopList.slice(0, detailed ? 12 : 4).forEach((stop, index) => {
        if (!Number.isFinite(stop.lat) || !Number.isFinite(stop.lng)) return;
        bounds.push([stop.lat, stop.lng]);
        L.marker([stop.lat, stop.lng], { icon: createStopIcon(index === 0) })
          .bindPopup(`<strong>${stop.name}</strong><br/><small>${formatDistance(stop)}</small>`)
          .addTo(layerRef.current);
      });

      // Bus-uri live care trec prin/pe lângă stație (extraVehicles)
      const mainStop = stopList[0] || { id: row.id, name: row.name, lat: row.lat, lng: row.lng, distanceKm: 0 };
      (extraVehicles || []).slice(0, 6).forEach((v) => {
        if (!Number.isFinite(v.lat) || !Number.isFinite(v.lng)) return;
        bounds.push([v.lat, v.lng]);
        const rt = String(v.route || v.lineRef || "bus");
        L.marker([v.lat, v.lng], { icon: createBusIcon(rt, v.bearing, false) })
          .bindPopup(`<strong>Tracked bus ${rt}</strong><br/><small>Live position</small>`)
          .addTo(layerRef.current);
      });

      if (hasBus) {
        bounds.push([estimate.vehicleLat, estimate.vehicleLng]);
        L.marker([estimate.vehicleLat, estimate.vehicleLng], { icon: createBusIcon(routeLabel, estimate.bearing, safeExpected) })
          .bindPopup(`<strong>Tracked bus ${routeLabel}</strong><br/><small>${estimate.distance || "Live position"}${safeExpected ? " · expected" : " · not confirmed next"}</small>`)
          .addTo(layerRef.current);
        if (mainStop?.lat && mainStop?.lng) {
          L.polyline([[estimate.vehicleLat, estimate.vehicleLng], [mainStop.lat, mainStop.lng]], {
            color: safeExpected ? "#16d37f" : "#f59e0b",
            weight: detailed ? 4 : 3,
            opacity: 0.85,
            dashArray: safeExpected ? null : "6 6",
          }).addTo(layerRef.current);
        }
      }

      // Taxi 🚕 pe hartă (lângă stație, dacă e disponibil)
      if (taxi?.ok && taxi.clearCount > 0 && mainStop?.lat && mainStop?.lng) {
        const taxiStop = { lat: mainStop.lat + 0.0015, lng: mainStop.lng + 0.0015 };
        L.marker([taxiStop.lat, taxiStop.lng], {
          // Alin's taxi icon (masina-uber-vector.svg): white car side profile
          // with dark windows + taxi roof sign, on a dark rounded badge.
          icon: L.divIcon({
            className: "",
            html: `<div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45));">
              <div style="
                width:48px;height:48px;border-radius:50%;
                background:#1a1a1a;border:2.5px solid #fff;
                display:flex;align-items:center;justify-content:center;
                box-shadow:0 2px 6px rgba(0,0,0,0.4);
              ">
                <svg width="34" height="34" viewBox="0 0 700 400" xmlns="http://www.w3.org/2000/svg">
                  <!-- rear wheel -->
                  <circle cx="190" cy="292" r="40" fill="#2b2b2b"/>
                  <circle cx="190" cy="292" r="26" fill="#f3f3f3"/>
                  <circle cx="190" cy="292" r="6" fill="#d0d0d0"/>
                  <!-- front wheel -->
                  <circle cx="490" cy="300" r="44" fill="#2b2b2b"/>
                  <circle cx="490" cy="300" r="28" fill="#fff"/>
                  <circle cx="490" cy="300" r="7" fill="#d0d0d0"/>
                  <!-- body -->
                  <path fill="#ffffff" d="M130 210 C135 160 170 125 230 112 C280 100 340 96 400 102 C470 110 530 132 575 168 C610 196 635 230 648 258 C656 276 650 292 630 300 C600 310 540 308 490 304 C430 300 360 298 280 292 C210 286 150 270 136 240 C130 228 128 218 130 210Z"/>
                  <!-- lower shadow strip -->
                  <path fill="#e6e6e6" d="M140 250 C160 272 230 288 320 294 C410 300 500 304 580 296 C620 292 640 282 642 268 C620 278 540 286 430 286 C320 286 210 274 150 258 C142 254 140 252 140 250Z"/>
                  <!-- windshield -->
                  <path fill="#2a2a2c" d="M392 112 C448 118 508 140 548 168 C528 186 498 200 460 206 C422 212 388 206 362 192 C370 162 380 132 392 112Z"/>
                  <!-- rear window -->
                  <path fill="#2a2a2c" d="M228 120 C262 108 300 104 338 108 C330 130 320 150 306 164 C274 156 246 146 226 138 C224 132 224 126 228 120Z"/>
                  <!-- mid window -->
                  <path fill="#2a2a2c" d="M348 112 C360 140 366 162 368 184 C348 178 326 172 306 166 C316 148 330 128 348 112Z"/>
                  <!-- pillars -->
                  <path d="M338 108 L306 166" stroke="#ffffff" stroke-width="7" fill="none"/>
                  <path d="M392 112 L368 184" stroke="#ffffff" stroke-width="8" fill="none"/>
                  <!-- door lines -->
                  <path d="M306 166 C300 200 300 236 308 276" stroke="#d5d5d5" stroke-width="2" fill="none"/>
                  <path d="M368 184 C366 220 368 252 376 286" stroke="#d8d8d8" stroke-width="2" fill="none"/>
                  <!-- handles -->
                  <rect x="322" y="214" width="18" height="5" rx="2" fill="#c8c8c8"/>
                  <rect x="402" y="218" width="18" height="5" rx="2" fill="#c8c8c8"/>
                  <!-- mirror -->
                  <ellipse cx="532" cy="186" rx="22" ry="14" fill="#efefef"/>
                  <ellipse cx="536" cy="186" rx="10" ry="7" fill="#2a2a2c"/>
                  <!-- front light bar -->
                  <path fill="#ffffff" stroke="#222" stroke-width="3" d="M575 198 C604 214 624 230 636 246 C620 248 598 236 576 220 C572 210 572 202 575 198Z"/>
                  <!-- rear lamp -->
                  <path fill="#cfcfcf" d="M142 188 C156 178 170 184 172 198 C160 208 144 204 142 188Z"/>
                  <!-- taxi sign -->
                  <rect x="352" y="58" width="78" height="40" rx="8" fill="#2a2a2a"/>
                  <rect x="360" y="66" width="62" height="24" rx="4" fill="#e7d9c4"/>
                </svg>
              </div>
            </div>`,
            iconSize: [48, 48],
            iconAnchor: [24, 24],
          }),
        }).bindPopup(`<div class="taxi-popup"><strong>Taxi Services</strong><span>${taxi.clearCount} drivers clear · ~${taxi.eta} min</span></div>`).addTo(layerRef.current);
        L.marker([mainStop.lat, mainStop.lng], {
          icon: L.divIcon({ className: "", html: ``, iconSize: [0,0] }),
        }).addTo(layerRef.current);
      }

      if (zoomOnStop && mainStop?.lat && mainStop?.lng) {
        // Zoom fix pe stație — nu fitBounds (care ar face zoom-out pe tot orașul)
        mapRef.current.setView([mainStop.lat, mainStop.lng], 17);
      } else if (bounds.length >= 2) {
        mapRef.current.fitBounds(bounds, { padding: detailed ? [42, 42] : [12, 12], maxZoom: detailed ? 17 : 16 });
      } else if (bounds.length === 1) {
        mapRef.current.setView(bounds[0], detailed ? 17 : 16);
      } else {
        mapRef.current.setView([50.3714, -4.1425], 13);
      }
      setTimeout(() => {
        mapRef.current?.invalidateSize();
        // Re-aplicăm zoom-ul fix pe stație DUPA invalidateSize, ca să nu fie suprascris de re-render/bus-uri
        if (zoomOnStop && mainStop?.lat && mainStop?.lng) {
          mapRef.current?.setView([mainStop.lat, mainStop.lng], 17, { animate: false });
        }
      }, 100);
    });
    return () => { destroyed = true; };
  }, [rowIdentity(row), detailed, hasBus, safeExpected, routeLabel, extraVehicles, taxi, zoomOnStop]);

  return <div ref={containerRef} className={detailed ? "real-service-map real-service-map--large" : "real-service-map real-service-map--mini"} />;
}

const COMPATIBLE_DIRECTIONS = {
  E: new Set(["E", "NE", "SE"]), W: new Set(["W", "NW", "SW"]),
  N: new Set(["N", "NE", "NW"]), S: new Set(["S", "SE", "SW"]),
  NE: new Set(["NE", "N", "E"]), NW: new Set(["NW", "N", "W"]),
  SE: new Set(["SE", "S", "E"]), SW: new Set(["SW", "S", "W"]),
};

function minutesUntilEndOfToday() {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return Math.max(0, Math.floor((end.getTime() - now.getTime()) / 60000));
}

function formatNextLabel(item) {
  if (!item || typeof item.minutes !== "number") return "Scheduled";
  const mins = item.minutes;
  if (mins <= 1) return "Due";
  if (mins < 60) return `${mins} mins`;
  if (mins < 120) return mins === 60 ? "1 hr" : `1 hr ${mins - 60} min`;
  const endOfToday = minutesUntilEndOfToday();
  if (mins <= endOfToday) return item.time || "Later today";
  if (mins <= endOfToday + 24 * 60) return `Tomorrow ${item.time}`;
  return item.time || "Later";
}

function formatDistance(stop) {
  if (stop?.distanceKm === undefined) return "Selected stop";
  return stop.distanceKm < 1 ? `${Math.round(stop.distanceKm * 1000)}m away` : `${stop.distanceKm.toFixed(1)}km away`;
}

function formatClockList(times = []) {
  return times.slice(0, 3).map((item, index) => {
    if (index === 0 && item.minutes > minutesUntilEndOfToday()) return `Tomorrow ${item.time}`;
    return item.time;
  }).join(" · ");
}

function getDirection(stopName = "") {
  const match = String(stopName).match(/\b(N|S|E|W|NE|NW|SE|SW)-bound\b/i);
  return match ? match[1].toUpperCase() : "";
}

function isSameSide(anchorStop, stop) {
  if (!anchorStop || !stop || anchorStop.id === stop.id) return true;
  const anchorDir = getDirection(anchorStop.name);
  const stopDir = getDirection(stop.name);
  if (!anchorDir || !stopDir) return true;
  return COMPATIBLE_DIRECTIONS[anchorDir]?.has(stopDir) ?? anchorDir === stopDir;
}

function sortRoutes(routes) {
  return Array.from(new Set(routes.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function RouteBadge({ route, routes }) {
  const values = routes?.length ? routes : [route];
  return (
    <div className="route-stack" aria-label={`Service ${values.join(" or ")}`}>
      {values.slice(0, 4).map((value) => (
        <div key={value} className="route-badge board-route-badge" style={{ background: ROUTE_COLOURS[value] || "#003087" }}>{value}</div>
      ))}
      {values.length > 4 && <span className="route-more">+{values.length - 4}</span>}
    </div>
  );
}

function TypePill({ type }) {
  const label = type || "Normal";
  return <span className={`type-pill ${TYPE_CLASS[label] || "type-normal"}`}>{label}</span>;
}

async function fetchStopServices(atco) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 65000);
  try {
    // Main board now uses GTFS stop_times. This is stricter than the old
    // TransXChange scanner because every departure is tied to an exact stop_id.
    const res = await fetch(`/api/gtfs-board?atco=${encodeURIComponent(atco)}`, { signal: controller.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.error || `GTFS board failed: ${res.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

function serviceToRow(service, stop) {
  const route = service.route;
  const destination = service.destination || "See timetable";
  const nextTimes = service.nextTimes || service.scheduledTimes || [];
  return {
    key: `${stop.id}-${route}-${destination}-${service.operatorName || service.operatorRef || ""}`,
    stop,
    route,
    routes: [route],
    destination,
    operator: service.operatorName || service.operatorRef || "Operator to confirm",
    serviceType: service.serviceType || "Normal",
    nextTimes,
    sortMinutes: nextTimes?.[0]?.minutes ?? 999999,
  };
}

function getExpectedIfSafe(row) {
  const first = row.nextTimes?.[0] || null;
  const estimate = row.liveEstimate || null;
  if (!first || !estimate) return null;
  if (!Number.isFinite(first.minutes) || !Number.isFinite(estimate.minutes)) return null;
  const closeToSchedule = Math.abs(estimate.minutes - first.minutes) <= 4;
  const routeMatches = (row.routes || [row.route]).some((route) => normaliseRoute(route) === normaliseRoute(estimate.routeMatched || row.route));
  const reliableConfidence = estimate.confidence !== "low";
  return closeToSchedule && routeMatches && reliableConfidence ? estimate : null;
}

function getBoardDue(row) {
  const first = row.nextTimes?.[0] || null;
  const expected = getExpectedIfSafe(row);
  if (expected) {
    return { label: expected.minutes <= 1 ? "Due" : `${expected.minutes} mins`, qualifier: "Expected", detail: first?.time ? `Scheduled ${first.time}` : "Live tracking agrees", source: "expected" };
  }
  if (first) return { label: formatNextLabel(first), qualifier: "Due", detail: formatClockList(row.nextTimes), source: "scheduled" };
  return null;
}

function BoardTimeCell({ row }) {
  const due = getBoardDue(row);
  if (!due) return <div className="simple-due simple-due--none"><strong>—</strong><span>No due time</span></div>;
  return (
    <div className={`simple-due simple-due--${due.source}`}>
      <strong>{due.label}</strong>
      <span>{due.qualifier}</span>
      <small>{due.detail}</small>
    </div>
  );
}

function SimpleBoardDue({ row }) {
  const due = getBoardDue(row);
  if (!due) return <span className="tv-due tv-due--none">—</span>;
  const label = due.label === "Due" ? "Due" : due.label;
  return <span className={`tv-due tv-due--${due.source}`}>{due.source === "expected" ? `Exp ${label}` : label}</span>;
}

function SimpleTvBoard({ rows, selectedStopOnly, loading, progress, fullScreen = false, onExitFullScreen, onRefresh, onRowClick }) {
  const time = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const maxRows = fullScreen ? 18 : 12;
  const densityClass = rows.length >= 10 ? "tv-board-card--dense" : rows.length <= 4 ? "tv-board-card--roomy" : "";
  return (
    <section className={`tv-board-card ${densityClass} ${fullScreen ? "tv-board-card--fullscreen" : ""}`} aria-label="Simple bus departure board">
      <div className="tv-board-top">
        <div className="tv-board-brand">Plymouth Bus Departures</div>
        {fullScreen && (
          <div className="tv-board-fullscreen-actions">
            <button type="button" onClick={onExitFullScreen}>Exit full page</button>
            <button type="button" onClick={onRefresh}>Refresh</button>
          </div>
        )}
        <div className="tv-board-clock">{time}</div>
      </div>
      <div className={`tv-board-head ${selectedStopOnly ? "" : "tv-board-head--area"}`}>
        <span>Service</span>
        <span>Destination</span>
        <span>Company</span>
        <span>Due</span>
        {!selectedStopOnly && <span>Stop</span>}
      </div>
      {loading && !rows.length && (
        <div className="tv-board-empty">Checking {progress.done}/{progress.total} stops…</div>
      )}
      {!loading && !rows.length && (
        <div className="tv-board-empty">No timed departures found for this stop.</div>
      )}
      <div className="tv-board-list">
        {rows.slice(0, maxRows).map((row) => {
          const key = `simple-${row.key}-${(row.routes || []).join("-")}-${row.destination}-${row.nextTimes?.[0]?.time || "x"}`;
          return (
            <div key={key} className={`tv-board-row ${selectedStopOnly ? "" : "tv-board-row--area"} ${onRowClick ? "tv-board-row--clickable" : ""}`} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              <div className="tv-service">{(row.routes || [row.route]).join("/")}</div>
              <div className="tv-destination">
                <strong>{row.destination}</strong>
              </div>
              <div className="tv-company-cell">{row.operator || "Operator"}</div>
              <div className="tv-due-cell"><SimpleBoardDue row={row} /></div>
              {!selectedStopOnly && <div className="tv-stop">{row.stop?.name?.replace(/\s+[A-Z]{1,2}-bound$/i, "") || "Nearby stop"}</div>}
            </div>
          );
        })}
      </div>
      <div className="tv-board-footer">
        Due times from GTFS timetable
      </div>
    </section>
  );
}

function addRowLive(row, vehicles) {
  const serviceForLive = { route: row.route, dest: row.destination, destination: row.destination };
  return {
    ...row,
    liveEstimate: getLiveEstimateForService(serviceForLive, row.stop, vehicles),
    liveRouteCount: countRouteVehicles(serviceForLive, vehicles),
  };
}

function makeBoardKey(row, selectedStopOnly) {
  const destinationKey = String(row.destination || "").toUpperCase().replace(/\s+/g, " ").trim();
  const operatorKey = String(row.operator || "").toUpperCase().replace(/\s+/g, " ").trim();
  // Selected-stop mode should behave like a normal bus stop departure board.
  // Local-area mode intentionally groups the same service/time across nearby stops,
  // otherwise the same route appears six times down one road and confuses users.
  const stopKey = selectedStopOnly ? row.stop.id : "LOCAL_AREA";
  const routeKey = String(row.route || "").toUpperCase().replace(/\s+/g, " ").trim();
  return [stopKey, routeKey, destinationKey, operatorKey].join("|");
}

function groupBoardRows(rows, selectedStopOnly) {
  const map = new Map();
  for (const row of rows) {
    const key = makeBoardKey(row, selectedStopOnly);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...row, routes: [row.route], stops: [row.stop] });
      continue;
    }
    existing.routes = sortRoutes([...(existing.routes || [existing.route]), row.route]);
    existing.route = existing.routes.join("/");
    existing.stops = [...(existing.stops || [existing.stop]), row.stop]
      .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
      .filter((stop, index, arr) => arr.findIndex((s) => s.id === stop.id) === index);
    existing.stop = existing.stops[0];
    const mergedTimes = [...(existing.nextTimes || []), ...(row.nextTimes || [])]
      .filter((item) => item && Number.isFinite(item.minutes))
      .sort((a, b) => a.minutes - b.minutes)
      .filter((item, index, arr) => arr.findIndex((other) => other.time === item.time && other.minutes === item.minutes) === index)
      .slice(0, 3);
    existing.nextTimes = mergedTimes;
    existing.scheduledTimes = mergedTimes;
    existing.serviceType = existing.serviceType === "School" || row.serviceType === "School" ? "School" : existing.serviceType;
    if (!existing.liveEstimate && row.liveEstimate) existing.liveEstimate = row.liveEstimate;
    if ((row.liveRouteCount || 0) > (existing.liveRouteCount || 0)) existing.liveRouteCount = row.liveRouteCount;
    map.set(key, existing);
  }
  return Array.from(map.values()).map((row) => ({ ...row, routes: sortRoutes(row.routes || [row.route]) }));
}

function StopCell({ row, selectedStopOnly }) {
  if (selectedStopOnly || !row.stops || row.stops.length <= 1) {
    return <div className="train-stop-cell"><strong>{row.stop.name}</strong><small>{formatDistance(row.stop)}</small></div>;
  }
  return (
    <div className="train-stop-cell">
      <strong>{row.stops.length} nearby stops</strong>
      <small>Nearest: {row.stops[0].name} · {formatDistance(row.stops[0])}</small>
      <div className="stop-chip-list">
        {row.stops.slice(0, 4).map((stop) => <span key={stop.id}>{stop.name.replace(/\s+[A-Z]{1,2}-bound$/i, "")}</span>)}
        {row.stops.length > 4 && <span>+{row.stops.length - 4} more</span>}
      </div>
    </div>
  );
}

function getMapPoints(row) {
  const estimate = row.liveEstimate || {};
  const stopList = row.stops?.length ? row.stops : [row.stop].filter(Boolean);
  const points = [];
  stopList.forEach((stop) => {
    if (Number.isFinite(stop?.lat) && Number.isFinite(stop?.lng)) points.push({ type: "stop", lat: stop.lat, lng: stop.lng, label: stop.name, stop });
  });
  if (Number.isFinite(estimate?.vehicleLat) && Number.isFinite(estimate?.vehicleLng)) {
    points.push({ type: "bus", lat: estimate.vehicleLat, lng: estimate.vehicleLng, label: `Route ${(row.routes || [row.route]).join("/")}` });
  }
  return points;
}

function buildProjection(points, width = 140, height = 76, pad = 12) {
  const safe = points.length ? points : [{ lat: 50.3714, lng: -4.1425 }];
  const lats = safe.map((p) => p.lat);
  const lngs = safe.map((p) => p.lng);
  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  const lngMin = Math.min(...lngs);
  const lngMax = Math.max(...lngs);
  const latSpan = Math.max(latMax - latMin, 0.0018);
  const lngSpan = Math.max(lngMax - lngMin, 0.0018);
  return (lat, lng) => {
    const x = pad + ((lng - (lngMin - lngSpan * 0.18)) / (lngSpan * 1.36)) * (width - pad * 2);
    const y = height - pad - ((lat - (latMin - latSpan * 0.18)) / (latSpan * 1.36)) * (height - pad * 2);
    return { x: Math.max(pad, Math.min(width - pad, x)), y: Math.max(pad, Math.min(height - pad, y)) };
  };
}

function rowIdentity(row) {
  return `${(row.routes || [row.route]).join("-")}-${row.destination}-${row.stop?.id}-${row.nextTimes?.[0]?.time || "notime"}`;
}

function MiniBusMap({ row, onOpen }) {
  const estimate = row.liveEstimate;
  const safeExpected = Boolean(getExpectedIfSafe(row));

  return (
    <div className="mini-bus-map mini-bus-map--leaflet">
      <MapTilePanel row={row} detailed={false} />
      <div className="mini-map-caption mini-map-caption--leaflet">
        <strong>{estimate?.vehicleLat ? (safeExpected ? "Expected bus" : "Tracked bus") : row.liveRouteCount > 0 ? "Live buses tracked" : "Timetable only"}</strong>
        <span>
          {estimate?.vehicleLat
            ? `${estimate.distance || "Live position"}${safeExpected ? "" : " · not confirmed next"}`
            : row.liveRouteCount > 0
            ? `${row.liveRouteCount} on route · ETA unavailable`
            : "No matching live bus yet"}
        </span>
      </div>
      <button type="button" className="mini-map-open" onClick={onOpen}>Open larger map</button>
    </div>
  );
}

function DetailedServiceMap({ row, onClose }) {
  const estimate = row.liveEstimate || null;
  const due = getBoardDue(row);
  const safeExpected = Boolean(getExpectedIfSafe(row));
  const stop = row.stop;
  const osmUrl = estimate?.vehicleLat && estimate?.vehicleLng && stop?.lat && stop?.lng
    ? `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${estimate.vehicleLat}%2C${estimate.vehicleLng}%3B${stop.lat}%2C${stop.lng}`
    : `https://www.openstreetmap.org/?mlat=${stop.lat}&mlon=${stop.lng}#map=17/${stop.lat}/${stop.lng}`;

  return (
    <div className="service-map-detail service-map-detail--real">
      <div className="service-map-detail__header">
        <div>
          <strong>Route {(row.routes || [row.route]).join(" / ")} live map</strong>
          <span>{row.destination} · {row.stop.name}</span>
        </div>
        <button type="button" onClick={onClose}>Close map</button>
      </div>

      <div className="service-map-detail__body service-map-detail__body--real">
        <MapTilePanel row={row} detailed />
        <div className="service-map-facts">
          <div><span>Due</span><strong>{due?.label || "—"}</strong><small>{due?.source === "expected" ? "Expected from live + timetable" : due?.detail || "No confirmed time"}</small></div>
          <div><span>Live tracking</span><strong>{estimate?.vehicleLat ? (safeExpected ? "Expected" : "Seen") : "None"}</strong><small>{estimate?.distance || "No matching bus position"}</small></div>
          <div><span>Map detail</span><strong>Road map</strong><small>Shows the bus, stop and nearby stops on OpenStreetMap tiles.</small></div>
          <a href={osmUrl} target="_blank" rel="noreferrer">Open in OpenStreetMap</a>
        </div>
      </div>
    </div>
  );
}

export default function LocalAreaBoard({ anchorStop, stops = [], vehicles = [], onBack, hideStationMap = false }) {
  const [radiusKm, setRadiusKm] = useState(0.4);
  const [selectedStopOnly, setSelectedStopOnly] = useState(true);
  const [sameSideOnly, setSameSideOnly] = useState(true);
  const [showSchoolServices, setShowSchoolServices] = useState(false);
  const [showNoTimeRoutes, setShowNoTimeRoutes] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedMapKey, setExpandedMapKey] = useState(null);
  const [boardStyle, setBoardStyle] = useState("simple");
  const [fullPageBoard, setFullPageBoard] = useState(false);
  // build-id taxi-livebus v3
  const [taxi, setTaxi] = useState(null); // { clearCount, eta, nearestKm }

  // Taxi ETA from TaxiCRM — nearest CLEAR driver to this stop.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      // If the stop has no coordinates, show the "booked" fallback (no nearby taxis).
      if (!anchorStop?.lat || !anchorStop?.lng) {
        setTaxi({ ok: true, clearCount: 0, eta: null, nearestKm: null });
        return;
      }
      try {
        const url = `/api/taxi-eta?lat=${encodeURIComponent(anchorStop.lat)}&lng=${encodeURIComponent(anchorStop.lng)}`;
        const resp = await fetch(url);
        if (!resp.ok) {
          setTaxi({ ok: true, clearCount: 0, eta: null, nearestKm: null });
          return;
        }
        const data = await resp.json();
        if (!cancelled) setTaxi(data);
      } catch (_) {
        if (!cancelled) setTaxi({ ok: true, clearCount: 0, eta: null, nearestKm: null });
      }
    }
    load();
    const id = setInterval(load, 60000); // refresh taxi ETA every minute
    return () => { cancelled = true; clearInterval(id); };
  }, [anchorStop?.lat, anchorStop?.lng, refreshKey]);

  const localStops = useMemo(() => {
    if (!anchorStop?.lat || !anchorStop?.lng) return anchorStop ? [anchorStop] : [];
    if (selectedStopOnly) return [{ ...anchorStop, distanceKm: 0 }];
    const maxStops = MAX_STOPS_BY_RADIUS[radiusKm] || 14;
    const nearby = getNearbyStops(anchorStop.lat, anchorStop.lng, radiusKm, stops)
      .filter((stop) => stop.id)
      .filter((stop) => !sameSideOnly || isSameSide(anchorStop, stop))
      .slice(0, maxStops);
    const hasAnchor = nearby.some((stop) => stop.id === anchorStop.id);
    return hasAnchor ? nearby : [{ ...anchorStop, distanceKm: 0 }, ...nearby].slice(0, maxStops);
  }, [anchorStop, radiusKm, stops, sameSideOnly, selectedStopOnly]);

  useEffect(() => {
    let cancelled = false;
    if (!localStops.length) return undefined;
    setLoading(true);
    setRows([]);
    setErrors([]);
    setProgress({ done: 0, total: localStops.length });

    async function run() {
      const nextRows = [];
      const nextErrors = [];
      for (let index = 0; index < localStops.length; index += 1) {
        const stop = localStops[index];
        try {
          const data = await fetchStopServices(stop.id);
          const services = Array.isArray(data.services) ? data.services : [];
          nextRows.push(...services.map((service) => serviceToRow(service, stop)));
        } catch (error) {
          nextErrors.push(`${stop.name}: ${error.message}`);
        }
        if (!cancelled) {
          setRows([...nextRows]);
          setErrors([...nextErrors]);
          setProgress({ done: index + 1, total: localStops.length });
        }
      }
      if (!cancelled) setLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, [localStops, refreshKey]);

  const visibleRows = useMemo(() => {
    const withLive = rows.map((row) => addRowLive(row, vehicles));
    let filtered = showSchoolServices ? withLive : withLive.filter((row) => row.serviceType !== "School");
    if (!showNoTimeRoutes) filtered = filtered.filter((row) => row.nextTimes?.[0]);
    const grouped = groupBoardRows(filtered, selectedStopOnly);
    return grouped.sort((a, b) => {
      const aMins = a.nextTimes?.[0]?.minutes ?? 999999;
      const bMins = b.nextTimes?.[0]?.minutes ?? 999999;
      if (aMins !== bMins) return aMins - bMins;
      const routeCompare = String((a.routes || [a.route])[0]).localeCompare(String((b.routes || [b.route])[0]), undefined, { numeric: true });
      if (routeCompare !== 0) return routeCompare;
      return (a.stop.distanceKm ?? 0) - (b.stop.distanceKm ?? 0);
    });
  }, [rows, showSchoolServices, showNoTimeRoutes, vehicles, selectedStopOnly]);

  const schoolCount = rows.filter((row) => row.serviceType === "School").length;
  const timedRowsCount = rows.filter((row) => row.nextTimes?.[0]).length;
  const trackedCount = visibleRows.filter((row) => row.liveRouteCount > 0).length;
  const routeCount = new Set(visibleRows.flatMap((row) => row.routes || [row.route])).size;
  const stopCountWithServices = new Set(visibleRows.flatMap((row) => (row.stops || [row.stop]).map((stop) => stop.id))).size;
  const nextDue = visibleRows.find((row) => row.nextTimes?.[0]);

  useEffect(() => {
    document.body.classList.toggle("board-fullscreen-active", fullPageBoard);
    return () => document.body.classList.remove("board-fullscreen-active");
  }, [fullPageBoard]);

  if (!anchorStop) return null;

  if (fullPageBoard) {
    return (
      <div className="board-fullscreen-shell">
        <SimpleTvBoard rows={visibleRows} selectedStopOnly={selectedStopOnly} loading={loading} progress={progress} fullScreen onExitFullScreen={() => setFullPageBoard(false)} onRefresh={() => setRefreshKey((value) => value + 1)} />
      </div>
    );
  }

  return (
    <div className="area-board-shell">
      <button onClick={onBack} className="back-btn">← Back to stops</button>

      {!hideStationMap && (
      <section className="station-stop-map" aria-label="Bus stop map">
        <MapTilePanel row={{ ...anchorStop, stop: { id: anchorStop.id, name: anchorStop.name, lat: anchorStop.lat, lng: anchorStop.lng, distanceKm: 0 }, stops: [{ id: anchorStop.id, name: anchorStop.name, lat: anchorStop.lat, lng: anchorStop.lng, distanceKm: 0 }], routes: Array.from(new Set(visibleRows.flatMap((r) => r.routes || [r.route]))).filter(Boolean) }} detailed extraVehicles={vehicles} taxi={taxi} zoomOnStop />
        <div className="station-stop-map__routes">
          <span className="station-stop-map__label">Routes via this stop</span>
          <span className="station-stop-map__route-badges">
            {Array.from(new Set(visibleRows.flatMap((r) => r.routes || [r.route]))).filter(Boolean).sort((a,b)=>String(a).localeCompare(String(b), undefined, {numeric:true})).map((rt) => (
              <span key={rt} className="station-stop-map__route">{rt}</span>
            ))}
          </span>
        </div>
      </section>
      )}

      <section className="station-board-hero station-board-hero--compact">
        <div className="station-title">
          <div className="station-kicker">Bus departure board</div>
          <h2>{anchorStop.name}</h2>
        </div>
        <div className="station-stats station-stats--compact">
          <div><strong>{routeCount}</strong><span>routes</span></div>
          <div><strong>{nextDue?.nextTimes?.[0] ? formatNextLabel(nextDue.nextTimes[0]) : "—"}</strong><span>next bus</span></div>
        </div>
      </section>

      <section className="area-board-controls">
        <div className="radius-control-block">
          <span className="control-label">Board view</span>
          <div className="radius-pills" aria-label="Board view mode">
            <button className={`radius-pill ${selectedStopOnly ? "active" : ""}`} onClick={() => setSelectedStopOnly(true)} type="button">Selected stop</button>
            <button className={`radius-pill ${!selectedStopOnly ? "active" : ""}`} onClick={() => setSelectedStopOnly(false)} type="button">Local area</button>
          </div>
        </div>

        <div className="area-control-actions">
          {schoolCount > 0 && (
            <button className={`school-toggle school-toggle--switch ${showSchoolServices ? "on" : ""}`} onClick={() => setShowSchoolServices((value) => !value)} type="button" role="switch" aria-checked={showSchoolServices}>
              <span className="school-toggle__label">School Bus</span>
              <span className="school-switch" aria-hidden="true">
                <span className="school-switch__knob" />
              </span>
              <span className="school-toggle__state">{showSchoolServices ? "On" : "Off"}</span>
            </button>
          )}
        </div>
      </section>

      {boardStyle === "simple" ? (
        <>
          <div className="simple-board-actions"><button className="fullscreen-hero-btn" onClick={() => setFullPageBoard(true)} type="button">⛶ Fullscreen departure board</button></div>
          {taxi && taxi.ok && (
            <div className={`taxi-board-seg ${taxi.clearCount === 0 ? "taxi-board-seg--booked" : ""}`}>
              <div className="taxi-board-seg__l">
                <span className="taxi-board-seg__ic"><svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect x="9" y="1.6" width="6" height="1.8" rx="0.5" fill="#FFCC00"/><path d="M5.5 5 L7 3.2 C7.2 3 7.5 2.9 7.8 2.9 H16.2 C16.5 2.9 16.8 3 17 3.2 L18.5 5" fill="none" stroke="#fff" stroke-width="1.3" stroke-linecap="round"/><path d="M5 6.5 C5 5.7 5.7 5 6.5 5 H17.5 C18.3 5 19 5.7 19 6.5 V15 C19 17.2 17.2 19 15 19 H9 C6.8 19 5 17.2 5 15 Z" fill="#fff"/><rect x="6.5" y="7.5" width="4.6" height="3.6" rx="0.7" fill="#1a1a1a" opacity="0.85"/><rect x="12.9" y="7.5" width="4.6" height="3.6" rx="0.7" fill="#1a1a1a" opacity="0.85"/><rect x="6.5" y="12.5" width="11" height="2" rx="0.7" fill="#1a1a1a" opacity="0.85"/><circle cx="8.5" cy="19" r="1.9" fill="#FFCC00"/><circle cx="15.5" cy="19" r="1.9" fill="#FFCC00"/></svg></span>
                <span className="taxi-board-seg__name">Taxi Services</span>
                <span className="taxi-board-seg__sub">{taxi.clearCount > 0 ? `${taxi.clearCount} drivers clear` : "All taxis are booked"}</span>
              </div>
              <div className="taxi-board-seg__r">
                <span className="taxi-board-seg__eta">{taxi.clearCount > 0 ? (taxi.eta ? `~${taxi.eta} min` : "—") : "—"}</span>
                <span className="taxi-board-seg__eta-label">{taxi.clearCount > 0 ? "ETA pick-up" : "No taxi available"}</span>
              </div>
            </div>
          )}
          <SimpleTvBoard rows={visibleRows} selectedStopOnly={selectedStopOnly} loading={loading} progress={progress} onRowClick={(row) => {
            const serviceKey = `simple-${row.key}-${(row.routes || []).join("-")}-${row.destination}-${row.nextTimes?.[0]?.time || "x"}`;
            setExpandedMapKey(expandedMapKey === serviceKey ? null : serviceKey);
          }} />
        </>
      ) : (
      <section className="train-board-card">
        <div className="train-board-topline">
          <div><span className="board-dot" /> Bus departures</div>
          <small>{loading ? `Checking ${progress.done}/${progress.total} ${selectedStopOnly ? "stop" : "stops"}…` : `Updated ${new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}</small>
        </div>

        <div className="train-board-head train-board-head--maps">
          <span>Service</span><span>Destination</span><span>{selectedStopOnly ? "Stop" : "Nearby stops"}</span><span>Due</span><span>Live bus</span>
        </div>

        {loading && !visibleRows.length && <div className="board-empty dark">Building departure board…<br />Checking exact timetable services.</div>}
        {!loading && !visibleRows.length && <div className="board-empty dark">No GTFS timed departures found for this stop. Check that data/gtfs.zip or BODS_GTFS_URL is configured, or try another stop.</div>}

        {visibleRows.map((row) => {
          const key = `${row.key}-${(row.routes || []).join("-")}-${(row.stops || []).map((s) => s.id).join("-")}`;
          const expanded = expandedMapKey === key;
          return (
            <div key={key} className="train-board-item">
              <div className={"train-board-row train-board-row--maps" + (expanded ? " train-board-row--expanded" : "")} role="button" tabIndex={0} onClick={() => setExpandedMapKey(expanded ? null : key)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpandedMapKey(expanded ? null : key); } }}>
                <RouteBadge route={row.route} routes={row.routes} />
                <div className="train-destination-cell"><strong>{row.destination}</strong><small>{row.operator}</small></div>
                <StopCell row={row} selectedStopOnly={selectedStopOnly} />
                <div className="train-time-cell"><BoardTimeCell row={row} /><div className="train-row-badges"><TypePill type={row.serviceType} /><span className="verified-label">Verified</span></div></div>
                <MiniBusMap row={row} onOpen={() => setExpandedMapKey(expanded ? null : key)} />
              </div>
              {expanded && <DetailedServiceMap row={row} onClose={() => setExpandedMapKey(null)} />}
            </div>
          );
        })}
      </section>
      )}

      {errors.length > 0 && <div className="info-box info-box--amber">Some stops could not be checked yet. {errors.slice(0, 2).join(" · ")}</div>}
      <p className="data-credit">GTFS mode: due times come from stop_times.txt for the exact stop. Live BODS vehicles are shown as map context only, not as the main timetable source.</p>
    </div>
  );
}
